import { HookStylePicker } from '../components/HookStylePicker';
import { MusicPreview } from '../components/MusicPreview';
import { SubtitlePositionControl } from '../components/SubtitlePositionControl';
import { AudioControls } from '../components/AudioControls';
import { MusicVolumeControl } from '../components/MusicVolumeControl';
import { TransitionControls } from '../components/TransitionControls';
import { MusicRights } from '../components/MusicRights';
import { musicAttribution, publicationText } from '@reels/shared/music';
/**
 * Результат и повторный монтаж. Все изменения копятся в локальном черновике и
 * уходят одним PATCH /projects/:id/edl, после чего запускается только рендер
 * (POST /projects/:id/rerender) — без расшифровки и модели.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { HIGHLIGHT_COLORS, HIGHLIGHT_COLOR_INFO, LIMITS, SUBTITLE_PRESET_CHOICES, SUBTITLE_LABELS } from '@reels/shared/constants';
import { clampCutawayShift, applySubtitleCorrections, remapWords, retainedSpeechWords, storyWords, TimelineMap, virtualizeStory, type SubtitlePreset } from '@reels/shared';
import { sourceUsage } from '@reels/shared/source-usage';
import { ColorControls } from '../components/ColorControls';
import { ActionButton } from '../components/ActionButton';
import { Chips, SettingRow, Switch } from '../components/Controls';
import { SubtitlePreview } from '../components/SubtitlePreview';
import { EmptyState, ErrorState, OfflineBanner } from '../components/States';
import { AssetRow, Dropzone, UploadRow } from '../components/Uploads';
import { highlightHex, previewFontFamily } from '../lib/subtitles';
import * as telegram from '../lib/telegram';
import { ACCEPT_ILLUSTRATION, formatDuration } from '../lib/video';
import { cutawayOperations, type CutawayDraft } from '../lib/cutaway-draft';
import type { EdlPatch } from '../lib/types';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import { ls, t } from '../i18n';

const SHIFT_STEP_S = 1.5;

interface Correction {
  from: string;
  to: string;
}

export function Result(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const project = state.project;
  const edl = project?.edl ?? null;

  const [hookStyle, setHookStyle] = useState(state.settings.hookStyle);
  const [hookEnabled, setHookEnabled] = useState(edl?.hook.enabled ?? false);
  const [hookText, setHookText] = useState(edl?.hook.text ?? '');
  const [coverTitle, setCoverTitle] = useState(edl?.cover.title ?? '');
  const [preset, setPreset] = useState<SubtitlePreset | 'off'>(
    edl ? (edl.subtitles.enabled ? edl.subtitles.preset : 'off') : 'karaoke',
  );
  const [subtitleSound, setSubtitleSound] = useState(state.settings.subtitleSound);
  const [subtitlePosition, setSubtitlePosition] = useState(state.settings.subtitlePosition);
  const [hookPosition, setHookPosition] = useState(state.settings.hookPosition);
  const [music, setMusic] = useState(edl?.music.enabled ?? true);
  const [noiseReduction, setNoiseReduction] = useState(state.settings.noiseReduction);
  const [musicVolume, setMusicVolume] = useState(state.settings.musicVolume);
  const [transition, setTransition] = useState(state.settings.transition);
  const [transitionDuration, setTransitionDuration] = useState(state.settings.transitionDuration);
  const [colorFilter, setColorFilter] = useState(state.settings.colorFilter);
  const [colorIntensity, setColorIntensity] = useState(state.settings.colorIntensity);
  const [highlight, setHighlight] = useState(state.settings.highlight);
  const [accentZooms, setAccentZooms] = useState(state.settings.accentZooms);
  const [caption, setCaption] = useState(edl?.publish.caption ?? '');
  const [hashtags, setHashtags] = useState((edl?.publish.hashtags ?? []).join(' '));
  // Шрифт и цвет подсветки после монтажа не меняются, показываем те, с которыми рендерили
  const previewFont = previewFontFamily(state.settings.font);
  const previewHighlight = highlightHex(highlight);
  const [corrections, setCorrections] = useState<Correction[]>(edl?.subtitles.corrections ?? []);
  const [fixFrom, setFixFrom] = useState('');
  const [fixTo, setFixTo] = useState('');
  const [ops, setOps] = useState<Record<number, CutawayDraft>>({});
  const illustrationInput = useRef<HTMLInputElement>(null);

  // Когда приходит новая версия EDL (после перерендера или перезагрузки),
  // черновик правок переинициализируется.
  const edlVersion = project?.edlVersion ?? null;
  useEffect(() => {
    if (!edl) return;
    setHookText(edl.hook.text);
    setHookEnabled(edl.hook.enabled);
    setHookStyle(state.settings.hookStyle);
    setCoverTitle(edl.cover.title);
    setPreset(edl.subtitles.enabled ? edl.subtitles.preset : 'off');
    setMusic(edl.music.enabled);
    setSubtitlePosition(state.settings.subtitlePosition);
    setHookPosition(state.settings.hookPosition);
    setSubtitleSound(state.settings.subtitleSound);
    setAccentZooms(state.settings.accentZooms);
    setNoiseReduction(state.settings.noiseReduction);
    setMusicVolume(state.settings.musicVolume);
    setTransition(state.settings.transition);
    setTransitionDuration(state.settings.transitionDuration);
    setColorFilter(state.settings.colorFilter);
    setColorIntensity(state.settings.colorIntensity);
    setHighlight(state.settings.highlight);
    setCaption(edl.publish.caption);
    setHashtags((edl.publish.hashtags ?? []).join(' '));
    setCorrections(edl.subtitles.corrections ?? []);
    setOps({});
     
  }, [project?.id, edlVersion]);

  const visibleSubtitleWords = useMemo(() => {
    if (!edl || edl.version !== 2 || !edl.sources) return null;
    const virtual = virtualizeStory(edl);
    return remapWords(retainedSpeechWords(virtual, storyWords(edl.sources)), new TimelineMap(virtual.timeline));
  }, [edl]);
  const correctionEffect = useMemo(() => visibleSubtitleWords
    ? applySubtitleCorrections(visibleSubtitleWords, corrections) : null,
  [visibleSubtitleWords, corrections]);

  const dirty = useMemo(() => {
    if (!edl) return false;
    const presetChanged = (edl.subtitles.enabled ? edl.subtitles.preset : 'off') !== preset;
    const hashtagsChanged = (edl.publish.hashtags ?? []).join(' ') !== hashtags.trim();
    const correctionsChanged =
      JSON.stringify(edl.subtitles.corrections ?? []) !== JSON.stringify(corrections);
    return (
      hookEnabled !== edl.hook.enabled ||
      hookText !== edl.hook.text ||
      hookStyle !== state.settings.hookStyle ||
      coverTitle !== edl.cover.title ||
      presetChanged ||
      subtitleSound !== state.settings.subtitleSound ||
      subtitlePosition !== state.settings.subtitlePosition ||
      hookPosition !== state.settings.hookPosition ||
      accentZooms !== state.settings.accentZooms ||
      noiseReduction !== state.settings.noiseReduction ||
      musicVolume !== state.settings.musicVolume ||
      transition !== state.settings.transition || transitionDuration !== state.settings.transitionDuration ||
      colorFilter !== state.settings.colorFilter || colorIntensity !== state.settings.colorIntensity || highlight !== state.settings.highlight ||
      music !== edl.music.enabled ||
      caption !== edl.publish.caption ||
      hashtagsChanged ||
      correctionsChanged ||
      cutawayOperations(ops).length > 0
    );
  }, [hookEnabled, hookStyle, state.settings.hookStyle, subtitleSound, state.settings.subtitleSound, subtitlePosition, state.settings.subtitlePosition, hookPosition, state.settings.hookPosition, noiseReduction, state.settings.noiseReduction, musicVolume, state.settings.musicVolume, transition, transitionDuration, state.settings.transition, state.settings.transitionDuration, colorFilter, colorIntensity, highlight, state.settings.highlight, state.settings.colorFilter, state.settings.colorIntensity, caption, corrections, coverTitle, edl, hashtags, hookText, music, ops, preset, accentZooms, state.settings.accentZooms]);

  if (!project) {
    return (
      <ErrorState
        message={t('result.notFound')}
        retryLabel={t('result.toProjects')}
        onRetry={() => dispatch({ type: 'route', screen: 'projects' })}
      />
    );
  }

  if (project.status === 'expired') {
    return (
      <>
        <h1>{t('result.expired.title')}</h1>
        <div className="banner">
          {t('result.expired.text')}
        </div>
        <ActionButton
          text={t('result.newProject')}
          onClick={() => {
            dispatch({ type: 'project/set', project: null });
            dispatch({ type: 'route', screen: 'editor' });
          }}
        />
      </>
    );
  }

  if (!edl) {
    return (
      <>
        <h1>{t('result.title.plain')}</h1>
        <EmptyState
          title={t('result.noEdl.title')}
          hint={t('result.noEdl.hint')}
          action={
            <button type="button" className="btn" onClick={() => void actions.reloadProject()}>
              {t('result.refresh')}
            </button>
          }
        />
      </>
    );
  }

  // Иллюстрации, добавленные после монтажа: файлы этого захода и неразобранные
  // ассеты (страница открыта заново). В ролик их ставит только новый монтаж.
  const illustrationUploads = state.uploads.filter(item => item.slot === 'illustration');
  const uploadedIds = new Set(illustrationUploads.map(item => item.assetId).filter((id): id is string => id !== null));
  const projectIllustrations = project.assets.filter(asset => asset.kind !== 'reaction' && !uploadedIds.has(asset.id));
  const addedIllustrations = projectIllustrations.filter(asset => !asset.scenes?.length);
  const illustrationCount = projectIllustrations.length + illustrationUploads.length;
  const newIllustrations = addedIllustrations.length + illustrationUploads.length > 0;
  const illustrationsUploading = illustrationUploads.some(item => ['checking', 'queued', 'uploading'].includes(item.status));
  const illustrationsReady = illustrationUploads.every(item => item.status === 'done') && addedIllustrations.every(asset => asset.uploaded !== false);
  const canEditSources = project.sourcesAvailable !== false;

  const totalDuration = new TimelineMap(edl.timeline).totalDuration;
  const render = project.render;
  const removeWatermark = Boolean(project.watermarked) && project.sourcesAvailable !== false;
  const cost = project.correctionCost === 1 ? t('result.cost') : '';
  const videoUrl = render?.downloadUrl ?? null;
  const fileName = `reels-${project.id}.mp4`;

  const setOp = (index: number, patch: Partial<CutawayDraft>): void => {
    setOps(current => ({ ...current, [index]: { ...current[index], ...patch } }));
  };

  const shiftCutaway = (index: number, step: number): void => {
    setOps(current => {
      const op = current[index];
      if (op?.removed) return current;
      const deltaS = clampCutawayShift(edl.cutaways[index]!, (op?.deltaS ?? 0) + step, totalDuration);
      return { ...current, [index]: { ...op, deltaS } };
    });
  };

  const buildPatch = (): EdlPatch => {
    const patch: EdlPatch = {};
    if (hookStyle !== state.settings.hookStyle || subtitleSound !== state.settings.subtitleSound || subtitlePosition !== state.settings.subtitlePosition || hookPosition !== state.settings.hookPosition || accentZooms !== state.settings.accentZooms ||
      noiseReduction !== state.settings.noiseReduction || musicVolume !== state.settings.musicVolume ||
      transition !== state.settings.transition || transitionDuration !== state.settings.transitionDuration ||
      colorFilter !== state.settings.colorFilter || colorIntensity !== state.settings.colorIntensity || highlight !== state.settings.highlight) {
      patch.effects = { hookStyle, subtitleSound, subtitlePosition, hookPosition, noiseReduction, musicVolume, accentZooms, colorFilter, colorIntensity, highlight, transition, transitionDuration };
    }
    if (hookText !== edl.hook.text || hookEnabled !== edl.hook.enabled) patch.hook = { text: hookText, enabled: hookEnabled };
    if (coverTitle !== edl.cover.title) patch.cover = { title: coverTitle };
    const presetNow = edl.subtitles.enabled ? edl.subtitles.preset : 'off';
    if (preset !== presetNow) {
      patch.subtitles =
        preset === 'off' ? { enabled: false } : { enabled: true, preset };
    }
    if (music !== edl.music.enabled) patch.music = { enabled: music };
    if (JSON.stringify(corrections) !== JSON.stringify(edl.subtitles.corrections ?? [])) {
      patch.corrections = corrections;
    }
    const tags = hashtags
      .split(/[\s,]+/)
      .map((tag) => tag.replace(/^#/, '').trim())
      .filter((tag) => tag.length > 0);
    const publish: { caption?: string; hashtags?: string[] } = {};
    if (caption !== edl.publish.caption) publish.caption = caption;
    if (tags.join(' ') !== (edl.publish.hashtags ?? []).join(' ')) publish.hashtags = tags;
    if (Object.keys(publish).length > 0) patch.publish = publish;
    const opList = cutawayOperations(ops);
    if (opList.length > 0) patch.cutaways = opList;
    return patch;
  };

  const apply = async (): Promise<void> => {
    const previous = [...edl.subtitles.corrections];
    const added = corrections.map((correction, index) => {
      const oldIndex = previous.findIndex(item => item.from === correction.from && item.to === correction.to);
      if (oldIndex >= 0) { previous.splice(oldIndex, 1); return -1; }
      return index;
    }).filter(index => index >= 0);
    if (added.length > 0 && preset === 'off') {
      actions.toast(t('result.apply.toast.subtitlesOff'));
      return;
    }
    const missingIndex = added.find(index => correctionEffect?.matches[index] === 0);
    if (missingIndex !== undefined) {
      actions.toast(t('result.apply.toast.phraseMissing', { phrase: corrections[missingIndex]!.from }));
      return;
    }
    const unchangedIndex = added.find(index => correctionEffect?.changes[index] === 0);
    if (unchangedIndex !== undefined) {
      const unchanged = corrections[unchangedIndex]!;
      actions.toast(t('result.apply.toast.unchanged', { from: unchanged.from, to: unchanged.to }));
      return;
    }
    const ok = await actions.applyEdlPatch(buildPatch(), { rerender: true });
    if (ok) setOps({});
  };

  // Оформление из черновика сохраняем до нового монтажа, остальные правки он всё равно пересоберёт.
  const remontageWithIllustrations = async (): Promise<void> => {
    const ok = await telegram.showConfirm(t('result.confirm.illustrations'));
    if (!ok) return;
    const effects = buildPatch().effects;
    if (effects && !(await actions.applyEdlPatch({ effects }, { rerender: false }))) return;
    await actions.remontage();
  };

  return (
    <>
      <h1>
        {t('result.title.lead')}<span className="accent">{t('result.title.accent')}</span>
      </h1>
      {!state.online ? <OfflineBanner /> : null}
      {edl.recovery ? <p className="card small" role="status">{edl.recovery.notice}</p> : null}

      {edl.version === 2 && edl.sources ? (
        <details className="card source-list">
          <summary>{t('result.sources.summary', { n: edl.sources.length })}</summary>
          {edl.sources.map((source, index) => {
            const usage = sourceUsage(edl, source.asset_id);
            return <div key={source.asset_id} className="col source-usage">
              <strong>{index + 1}. {source.filename}</strong>
              <span className="small muted">{usage.status === 'used' ? t('result.sources.used') : usage.status === 'partial' ? t('result.sources.partial', { duration: formatDuration(usage.usedS) }) : t('result.sources.unused')}</span>
              {usage.reason ? <span className="small muted">{usage.reason}</span> : null}
              {usage.status !== 'used' ? <button type="button" className="btn small" disabled={state.busy || dirty || newIllustrations} onClick={() => void actions.applyEdlPatch({ restoreSource: source.asset_id })}>{t('result.sources.restore')}</button> : null}
            </div>;
          })}
        </details>
      ) : null}
      {videoUrl ? (
        <video className="player" src={videoUrl} controls playsInline poster={render?.coverUrl ?? undefined} />
      ) : (
        <div className="card center">
          <SubtitlePreview
            preset={preset}
            position={subtitlePosition}
            colorFilter={colorFilter}
            colorIntensity={colorIntensity}
            hookEnabled={hookEnabled} hookStyle={hookStyle} hookAccent={state.settings.hookAccent} subtitleBox={state.settings.subtitleBox} subtitleSize={state.settings.subtitleSize}
            hookText={hookText}
            fontFamily={previewFont}
            highlight={previewHighlight}
          />
          <span className="small muted">
            {t('result.preview.unavailable')}
          </span>
          <button type="button" className="btn small" onClick={() => void actions.reloadProject()}>
            {t('result.refresh')}
          </button>
        </div>
      )}

      <div className="facts">
        <div className="fact">
          <span className="fact-label">{t('result.fact.duration')}</span>
          <span className="fact-value">{render?.durationS ? formatDuration(render.durationS) : '—'}</span>
        </div>
        <div className="fact">
          <span className="fact-label">{t('result.fact.format')}</span>
          <span className="fact-value">1080×1920</span>
        </div>
      </div>

      <div className="btn-row">
        <button
          type="button"
          className="btn grow"
          disabled={!videoUrl}
          onClick={() => void telegram.downloadFile(videoUrl ?? '', fileName)}
        >
          {t('result.download')}
        </button>
        <button
          type="button"
          className="btn grow"
          disabled={!videoUrl || !!musicAttribution(render?.music)}
          onClick={() => {
            const shared = telegram.shareToStory(videoUrl ?? '', caption.slice(0, 200));
            if (!shared) actions.toast(t('result.toStory.telegramOnly'));
          }}
        >
          {t('result.toStory')}
        </button>
      </div>

      {musicAttribution(render?.music) && <p className="muted small">{t('result.music.attribution')}</p>}
      <MusicRights music={render?.music} />
      <div className="divider" />
      <h2>{t('result.again.title')}</h2>
      <p className="muted small">
        {t('result.again.text')}
      </p>

      <div className="field card">
        <SettingRow title={t('result.hook.title')}><Switch checked={hookEnabled} onChange={setHookEnabled} label={t('result.hook.show')} /></SettingRow>
        <label htmlFor="hook">{t('result.hook.text')}</label>
        <input
          id="hook"
          type="text"
          value={hookText}
          maxLength={60}
          onChange={(event) => setHookText(event.target.value)}
          placeholder={t('result.hook.placeholder')}
        />
        <label htmlFor="cover">{t('result.cover.title')}</label>
        <input
          id="cover"
          type="text"
          value={coverTitle}
          maxLength={35}
          onChange={(event) => setCoverTitle(event.target.value)}
          placeholder={t('result.cover.placeholder')}
        />
      </div>

      {hookEnabled ? <SettingRow title={t('result.hookStyle.title')} hint={t('result.hookStyle.hint')} stacked>
        <HookStylePicker value={hookStyle} onChange={setHookStyle} text={hookText} accent={previewHighlight}
          accentMode={state.settings.hookAccent} fontFamily={previewFont} box={state.settings.subtitleBox === 'none' ? false : state.settings.subtitleBox} />
      </SettingRow> : null}

      <SettingRow title={t('result.subtitles.title')} stacked>
        <Chips
          ariaLabel={t('result.subtitles.style')}
          options={(['off', ...SUBTITLE_PRESET_CHOICES] as const).map((value) => ({
            value,
            label: ls(SUBTITLE_LABELS[value]),
          }))}
          value={preset}
          onChange={setPreset}
        />
        <SubtitlePreview
          preset={preset}
          position={subtitlePosition}
          colorFilter={colorFilter}
          colorIntensity={colorIntensity}
          hookEnabled={hookEnabled} hookStyle={hookStyle} hookAccent={state.settings.hookAccent} subtitleBox={state.settings.subtitleBox} subtitleSize={state.settings.subtitleSize}
          hookText={hookText}
          fontFamily={previewFont}
          highlight={previewHighlight}
          hookPosition={hookPosition}
          onPositionChange={typeof subtitlePosition === 'number' ? setSubtitlePosition : undefined}
          onHookPositionChange={typeof subtitlePosition === 'number' ? setHookPosition : undefined}
        />
      </SettingRow>

      {preset !== 'off' ? <SettingRow title={t('result.subtitleColor.title')} stacked>
        <Chips
          ariaLabel={t('editor.subtitles.highlight')}
          overlay
          className="swatches"
          options={HIGHLIGHT_COLORS.map(key => ({ value: key, label: ls(HIGHLIGHT_COLOR_INFO[key].label), swatch: HIGHLIGHT_COLOR_INFO[key].hex }))}
          value={highlight}
          onChange={setHighlight}
        />
      </SettingRow> : null}

      {preset !== 'off' ? <SubtitlePositionControl value={subtitlePosition} onChange={setSubtitlePosition} /> : null}

      {preset !== 'off' ? <SettingRow title={t('result.typing.title')} hint={t('result.typing.hint')}>
        <Switch checked={subtitleSound} onChange={setSubtitleSound} label={t('result.typing.title')} />
      </SettingRow> : null}

      <SettingRow title={t('result.color.title')} hint={t('result.color.hint')} stacked>
        <ColorControls preset={colorFilter} intensity={colorIntensity} onChange={patch => {
          if (patch.colorFilter !== undefined) setColorFilter(patch.colorFilter);
          if (patch.colorIntensity !== undefined) setColorIntensity(patch.colorIntensity);
        }} />
      </SettingRow>

      <SettingRow title={t('result.transitions.title')} hint={t('result.transitions.hint')} stacked>
        <TransitionControls type={transition} duration={transitionDuration} onChange={patch => {
          if (patch.transition !== undefined) setTransition(patch.transition);
          if (patch.transitionDuration !== undefined) setTransitionDuration(patch.transitionDuration);
        }} />
      </SettingRow>

      <SettingRow title={t('result.zooms.title')} hint={t('result.zooms.hint')}>
        <Switch checked={accentZooms} onChange={setAccentZooms} label={t('result.zooms.title')} />
      </SettingRow>
      <div className="card">
        <div className="row between">
          <div className="col grow">
            <h3>{t('result.music.title')}</h3>
            <span className="small muted">{t('result.music.hint')}</span>
          </div>
          <Switch checked={music} onChange={setMusic} label={t('result.music.title')} />
        </div>
        {music && <>
          <h3 className="music-volume-title">{t('result.music.volume')}</h3>
          <MusicVolumeControl value={musicVolume} onChange={setMusicVolume} />
        </>}
      </div>
      {music && <MusicPreview mood={edl.music.mood} />}
      <SettingRow title={t('result.noise.title')} hint={t('result.noise.hint')} stacked>
        <AudioControls mode={noiseReduction} onChange={setNoiseReduction} />
      </SettingRow>

      <div className="card">
        <h3>{t('result.cutaways.title')}</h3>
        {edl.cutaways.length === 0 ? (
          <span className="small muted">{t('result.cutaways.none')}</span>
        ) : null}
        {edl.cutaways.map((cutaway, index) => {
          const op = ops[index];
          const removed = !!op?.removed;
          const shifted = clampCutawayShift(cutaway, op?.deltaS ?? 0, totalDuration);
          const canShiftLeft = clampCutawayShift(cutaway, shifted - SHIFT_STEP_S, totalDuration) < shifted;
          const canShiftRight = clampCutawayShift(cutaway, shifted + SHIFT_STEP_S, totalDuration) > shifted;
          const replaced = op?.sceneId ?? null;
          return (
            <div key={`${cutaway.asset_id}-${index}`} className="col" style={{ gap: 6 }}>
              <div className="divider" />
              <div className="row between">
                <span className="small muted">
                  {formatDuration(cutaway.tl_in + shifted)}–{formatDuration(cutaway.tl_out + shifted)}
                  {shifted !== 0 ? t('result.cutaways.shift', { value: `${shifted > 0 ? '+' : ''}${shifted.toFixed(1)}` }) : ''}
                </span>
                {removed ? <span className="status failed">{t('result.cutaways.removedBadge')}</span> : null}
              </div>
              <span className="small">{cutaway.why}</span>

              <div className="btn-row">
                <button
                  type="button"
                  className="btn small"
                  disabled={removed || !canShiftLeft}
                  onClick={() => shiftCutaway(index, -SHIFT_STEP_S)}
                >
                  {t('result.cutaways.earlier')}
                </button>
                <button
                  type="button"
                  className="btn small"
                  disabled={removed || !canShiftRight}
                  onClick={() => shiftCutaway(index, SHIFT_STEP_S)}
                >
                  {t('result.cutaways.later')}
                </button>
                <button
                  type="button"
                  className={`btn small ${removed ? '' : 'danger'}`}
                  onClick={() => setOp(index, { removed: !removed })}
                >
                  {removed ? t('result.cutaways.restore') : t('result.cutaways.remove')}
                </button>
              </div>

              {project.candidates.length > 0 && !removed ? (
                <select
                  className="btn small"
                  value={replaced ?? ''}
                  onChange={(event) => {
                    const sceneId = event.target.value;
                    setOp(index, { sceneId: sceneId || undefined });
                  }}
                >
                  <option value="">{t('result.cutaways.keep')}</option>
                  {project.candidates.map((scene) => (
                    <option key={scene.id} value={scene.id}>
                      {scene.description}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          );
        })}
      </div>

      {canEditSources ? <div className="card field">
        <h3>{t('result.illustrations.title')}</h3>
        <span className="small muted">{t('result.illustrations.text')}</span>
        {projectIllustrations.length > addedIllustrations.length
          ? <span className="small muted">{t('result.illustrations.existing', { n: projectIllustrations.length - addedIllustrations.length })}</span> : null}
        {addedIllustrations.map(asset => (
          <AssetRow key={asset.id} asset={asset} onRemove={() => void actions.removeAsset(asset.id)} />
        ))}
        {illustrationUploads.map(item => (
          <UploadRow key={item.id} item={item}
            onRemove={() => void actions.removeUpload(item.id)}
            onRetry={() => void actions.retryUpload(item.id)} />
        ))}
        {illustrationCount < LIMITS.maxIllustrations ? (
          <Dropzone
            title={t('result.illustrations.add', { count: illustrationCount, max: LIMITS.maxIllustrations })}
            hint={t('result.illustrations.hint')}
            disabled={state.busy || illustrationsUploading || !state.online}
            onClick={() => illustrationInput.current?.click()}
          />
        ) : <span className="small muted">{t('result.illustrations.max')}</span>}
        <input
          ref={illustrationInput}
          className="hidden-input"
          type="file"
          multiple
          accept={ACCEPT_ILLUSTRATION}
          onChange={(event) => {
            const files = event.target.files;
            if (files && files.length > 0) void actions.addFiles(files, 'illustration');
            // Сбрасываем значение, иначе повторный выбор того же файла не сработает.
            event.target.value = '';
          }}
        />
      </div> : null}

      <div className="card">
        <h3>{t('result.fix.title')}</h3>
        <span className="small muted">{t('result.fix.text')}</span>
        <div className="row">
          <input
            type="text"
            className="grow"
            placeholder={t('result.fix.from')}
            value={fixFrom}
            onChange={(event) => setFixFrom(event.target.value)}
          />
          <input
            type="text"
            className="grow"
            placeholder={t('result.fix.to')}
            value={fixTo}
            onChange={(event) => setFixTo(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn small"
          disabled={fixFrom.trim().length === 0 || fixTo.trim().length === 0}
          onClick={() => {
            const next = [...corrections, { from: fixFrom.trim(), to: fixTo.trim() }];
            if (visibleSubtitleWords) {
              const effect = applySubtitleCorrections(visibleSubtitleWords, next);
              if (effect.matches.at(-1) === 0) {
                actions.toast(t('result.fix.toast.phraseDraft', { phrase: fixFrom.trim() }));
                return;
              }
              if (effect.changes.at(-1) === 0) {
                actions.toast(t('result.fix.toast.noChange'));
                return;
              }
            }
            setCorrections(next);
            setFixFrom('');
            setFixTo('');
          }}
        >
          {t('result.fix.add')}
        </button>
        {JSON.stringify(corrections) !== JSON.stringify(edl.subtitles.corrections ?? [])
          ? <span className="small muted">{t('result.fix.draft')}</span> : null}
        {corrections.map((correction, index) => (
          <div key={`${correction.from}-${index}`} className="row between">
            <span className="small">
              {correction.from} → {correction.to}
              {correctionEffect ? <span className="muted"> · {correctionEffect.matches[index] ? t('result.fix.matches', { n: correctionEffect.matches[index] }) : t('result.fix.notFoundBadge')}</span> : null}
            </span>
            <button
              type="button"
              className="btn small danger"
              onClick={() => setCorrections((list) => list.filter((_, position) => position !== index))}
            >
              {t('result.fix.remove')}
            </button>
          </div>
        ))}
      </div>

      <div className="card field">
        <h3>{t('result.publish.title')}</h3>
        <textarea
          rows={4}
          value={caption}
          maxLength={2200}
          onChange={(event) => setCaption(event.target.value)}
          placeholder={t('result.publish.caption')}
        />
        <input
          type="text"
          value={hashtags}
          onChange={(event) => setHashtags(event.target.value)}
          placeholder={t('result.publish.hashtags')}
        />
        <div className="tags">
          {hashtags
            .split(/[\s,]+/)
            .filter((tag) => tag.length > 0)
            .map((tag, index) => (
              <span key={`${tag}-${index}`} className="chip">
                #{tag.replace(/^#/, '')}
              </span>
            ))}
        </div>
        <div className="btn-row publish-actions">
          <button type="button" className="btn small publish-copy" onClick={() => {
            const tags = hashtags.split(/[\s,]+/).filter(Boolean).map((tag) => `#${tag.replace(/^#+/, '')}`).join(' ');
            void (async () => {
              try {
                await navigator.clipboard.writeText(publicationText(caption, tags, render?.music));
                actions.toast(t('result.publish.copied'));
              } catch {
                actions.toast(t('result.publish.copyFailed'));
              }
            })();
          }}>{t('result.publish.copy')}</button>

        </div>
      </div>

      {render?.delivered ? (
        <p className="muted small">{t('result.delivered')}</p>
      ) : (
        <button
          type="button"
          className="btn block"
          disabled={state.busy || !state.online}
          onClick={() => void actions.deliver()}
        >
          {t('result.deliver')}
        </button>
      )}

      {project.sourcesAvailable === false ? <p className="small muted">{t('result.sourcesGone')}</p> : null}
      {project.sourcesAvailable !== false && project.watermarked ? <p className="small muted">{t('result.watermarked.note')}</p>
        : project.sourcesAvailable !== false && project.correctionCost === 1 ? <p className="small muted">{t('result.trial.note')}</p> : null}
      {project.sourcesAvailable !== false && !dirty && !newIllustrations ? <>
        <button type="button" className="btn block" disabled={state.busy || !state.online}
          onClick={() => void telegram.showConfirm(t('result.newEdit.confirm'))
            .then((ok) => { if (ok) void actions.remontage(); })}>{t('result.newEdit')}{project.correctionCost === 1 ? t('result.cost') : ''}</button>
        <p className="small muted">{t('result.newEdit.hint')}</p>
      </> : null}
      {newIllustrations ? <ActionButton
        text={`${t('result.newEdit')}${cost}`}
        caption={illustrationsUploading ? t('result.illustrations.uploading') : !illustrationsReady ? t('result.illustrations.checkUploads') : undefined}
        enabled={canEditSources && illustrationsReady && !state.busy && state.online}
        loading={state.busy}
        onClick={() => void remontageWithIllustrations()}
      /> : <ActionButton
        text={dirty ? project.watermarked ? t('result.rebuild.applyUnmark') : `${t('result.rebuild.withChanges')}${cost}`
          : removeWatermark ? t('result.rebuild.removeMark') : `${t('result.rebuild.video')}${cost}`}
        enabled={project.sourcesAvailable !== false && !state.busy && state.online}
        loading={state.busy}
        onClick={() => {
          if (dirty) { void apply(); return; }
          // Без правок собираем ту же нарезку заново: с текущим оформлением и
          // исправлениями рендера. Пробный ролик при этом теряет знак.
          void telegram.showConfirm(removeWatermark
            ? t('result.rebuild.confirmMark')
            : `${t('result.rebuild.confirm')}${cost ? t('result.rebuild.confirmCost') : ''}`)
            .then((ok) => { if (ok) void actions.rerenderCurrent(); });
        }}
      />}
    </>
  );
}
